import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "../lib/api";

const MAX_POLLS = 10;

const STATES = {
  checking: { icon: Loader2, tone: "text-slate-500 animate-spin", title: "Confirming your payment…", body: "This usually takes a few seconds. Please keep this tab open." },
  paid: { icon: CheckCircle2, tone: "text-emerald-600", title: "Payment confirmed", body: "Your purchase is unlocked. The receipt is in your Payment history." },
  failed: { icon: XCircle, tone: "text-rose-500", title: "Payment didn't go through", body: "Your card wasn't charged. You can try again from the Billing page." },
  timeout: { icon: Clock, tone: "text-amber-500", title: "Still processing", body: "We haven't received confirmation yet. Check your Payment history in a minute." },
  missing: { icon: XCircle, tone: "text-slate-400", title: "Payment not found", body: "We couldn't find this checkout session. Head back to Billing to start again." },
  cancelled: { icon: XCircle, tone: "text-slate-400", title: "Checkout cancelled", body: "No charge was made. You can pick a plan again whenever you're ready." },
};

function ResultShell({ state, testid }) {
  const s = STATES[state];
  const Icon = s.icon;
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid={testid}>
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8">
        <div className="flex items-center gap-2 text-slate-900 font-display font-semibold">
          <ShieldCheck className="w-5 h-5" /> HavenGuard
        </div>
        <Icon className={`w-10 h-10 mt-8 ${s.tone}`} />
        <h1 className="mt-4 text-2xl font-display font-bold text-slate-900" data-testid="payment-result-title">{s.title}</h1>
        <p className="mt-2 text-sm text-slate-600" data-testid="payment-result-body">{s.body}</p>
        {state !== "checking" && (
          <Link to="/app/billing" data-testid="payment-result-back-btn"
            className="mt-8 inline-flex items-center justify-center w-full rounded-full bg-slate-900 text-white py-3 text-sm font-medium hover:bg-slate-800 transition-colors">
            Back to billing
          </Link>
        )}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState(sid ? "checking" : "missing");

  useEffect(() => {
    if (!sid) { return undefined; }
    let alive = true;
    const poll = async (attempt) => {
      if (!alive) { return; }
      if (attempt >= MAX_POLLS) { setState("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!alive) { return; }
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status !== "pending") { setState("failed"); return; }
      } catch (err) {
        if (err.response?.status === 404) { setState("missing"); return; }
      }
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll(0);
    return () => { alive = false; };
  }, [sid]);

  return <ResultShell state={state} testid="payment-success-page" />;
}

export function PaymentCancel() {
  return <ResultShell state="cancelled" testid="payment-cancel-page" />;
}
